import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { apiVariable } from "../../../api/apiVariable";
import useAuth from "../../../hooks/useAuth";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Box,
  CircularProgress,
  Typography,
  TextField,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { trucksTableColumns } from "../../../data/FormColumns/Fleet/trucksTableColumns";
import "./TrucksTable.css";
import { useNavigate, useSearchParams } from "react-router-dom";
import * as signalR from "@microsoft/signalr";
import PersistedDataGrid from "../../PersistedDataGrid/PersistedDataGrid";

const normalize = (value: unknown) =>
  (value ?? "").toString().trim().toLowerCase();

const normalizeWithSpaces = (value: unknown) =>
  normalize(value).replace(/[_-]/g, " ");

const parseNumberToken = (token: string): number | null => {
  const normalized = token.toLowerCase().replace(/,/g, "").trim();
  const match = normalized.match(/(\d+(?:\.\d+)?)(k|m)?/);
  if (!match) return null;

  const base = Number(match[1]);
  if (Number.isNaN(base)) return null;
  const suffix = match[2];

  if (suffix === "k") return base * 1_000;
  if (suffix === "m") return base * 1_000_000;
  return base;
};

const matchRangeLabel = (
  value: number | null | undefined,
  label: string | null,
): boolean => {
  if (!label) return true;

  // Preserve hyphens so range labels like "0-2 yrs" or "0-250k" keep their
  // delimiters, while still normalizing case and underscores.
  const normalizedLabel = normalize(label)
    .replace(/_/g, " ")
    .replace(/[–—]/g, "-");
  if (
    value == null &&
    (normalizedLabel.includes("unknown") ||
      normalizedLabel.includes("n/a") ||
      normalizedLabel.includes("no data"))
  ) {
    return true;
  }

  if (value == null) return false;

  const rangeMatch = normalizedLabel.match(
    /(\d+(?:\.\d+)?\s*[km]?)\s*(?:-|to)\s*(\d+(?:\.\d+)?\s*[km]?)/,
  );
  if (rangeMatch) {
    const min = parseNumberToken(rangeMatch[1]);
    const max = parseNumberToken(rangeMatch[2]);
    if (min == null || max == null) return false;
    return value >= min && value <= max;
  }

  const plusMatch = normalizedLabel.match(/(\d+(?:\.\d+)?\s*[km]?)\s*\+/);
  if (plusMatch) {
    const min = parseNumberToken(plusMatch[1]);
    if (min == null) return false;
    return value >= min;
  }

  const andAboveMatch = normalizedLabel.match(
    /(\d+(?:\.\d+)?\s*[km]?)\s*(?:and above|or above|and up|or more|above)/,
  );
  if (andAboveMatch) {
    const min = parseNumberToken(andAboveMatch[1]);
    if (min == null) return false;
    return value >= min;
  }

  const exactMatch = normalizedLabel.match(/^(\d+(?:\.\d+)?\s*[km]?)$/);
  if (exactMatch) {
    const exact = parseNumberToken(exactMatch[1]);
    if (exact == null) return false;
    return value === exact;
  }

  const lessThanMatch = normalizedLabel.match(
    /(?:<|<=|under|below|up to)\s*(\d+(?:\.\d+)?\s*[km]?)/,
  );
  if (lessThanMatch) {
    const max = parseNumberToken(lessThanMatch[1]);
    if (max == null) return false;
    return value <= max;
  }

  const greaterThanMatch = normalizedLabel.match(
    /(?:>|>=|over|more than)\s*(\d+(?:\.\d+)?\s*[km]?)/,
  );
  if (greaterThanMatch) {
    const min = parseNumberToken(greaterThanMatch[1]);
    if (min == null) return false;
    return value >= min;
  }

  const betweenMatch = normalizedLabel.match(
    /between\s+(\d+(?:\.\d+)?\s*[km]?)\s+and\s+(\d+(?:\.\d+)?\s*[km]?)/,
  );
  if (betweenMatch) {
    const min = parseNumberToken(betweenMatch[1]);
    const max = parseNumberToken(betweenMatch[2]);
    if (min == null || max == null) return false;
    return value >= min && value <= max;
  }

  return false;
};

const matchFleetAgeBucket = (
  age: number | null | undefined,
  label: string | null,
): boolean => {
  if (!label) return true;
  if (age == null) return false;

  const normalizedLabel = normalize(label).replace(/[–—]/g, "-");

  if (/^0\s*-\s*2(?:\s*(?:yrs?|years?))?$/.test(normalizedLabel)) {
    return age >= 0 && age <= 2;
  }

  if (/^3\s*-\s*4(?:\s*(?:yrs?|years?))?$/.test(normalizedLabel)) {
    return age >= 3 && age <= 4;
  }

  if (/^5\s*-\s*6(?:\s*(?:yrs?|years?))?$/.test(normalizedLabel)) {
    return age >= 5 && age <= 6;
  }

  if (/^7\s*\+(?:\s*(?:yrs?|years?))?$/.test(normalizedLabel)) {
    return age >= 7;
  }

  return matchRangeLabel(age, label);
};

const matchFleetOdometerBucket = (
  mileage: number | null | undefined,
  label: string | null,
): boolean => {
  if (!label) return true;
  if (mileage == null) return false;

  const normalizedLabel = normalize(label).replace(/[–—]/g, "-");

  if (/^0\s*-\s*150k(?:\s*(?:mi|miles?))?$/.test(normalizedLabel)) {
    return mileage >= 0 && mileage < 150_000;
  }

  if (/^150\s*-\s*300k(?:\s*(?:mi|miles?))?$/.test(normalizedLabel)) {
    return mileage >= 150_000 && mileage < 300_000;
  }

  if (/^350\s*-\s*700k(?:\s*(?:mi|miles?))?$/.test(normalizedLabel)) {
    return mileage >= 350_000 && mileage < 700_000;
  }

  if (/^700k\+(?:\s*(?:mi|miles?))?$/.test(normalizedLabel)) {
    return mileage >= 700_000;
  }

  return matchRangeLabel(mileage, label);
};

const fetchTrucks = async (
  token: string | null | undefined,
  areActive: boolean,
) => {
  const { data } = await axios.get(`${apiVariable}/getAllTrucks`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    params: { areActive },
  });
  return data;
};

const TrucksTable = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const pageFromUrl = parseInt(searchParams.get("page") || "0", 10);
  const statusFromUrl = searchParams.get("status");
  const transactionTypeFromUrl = searchParams.get("transactionType");
  const excludeStatuses = (searchParams.get("excludeStatuses") || "")
    .split(",")
    .map((status) => normalize(status))
    .filter(Boolean);
  const ageBucketFromUrl = searchParams.get("ageBucket");
  const odometerBucketFromUrl = searchParams.get("odometerBucket");
  const makeFromUrl = searchParams.get("make");
  const companyIdFromUrl = searchParams.get("companyId");
  const filterByAuthorityCompanyIdFromUrl =
    searchParams.get("filterByAuthorityCompanyId") === "true";
  const areActiveFromUrl = searchParams.get("areActive");
  const [areActive, setAreActive] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  const applyFilterModel = undefined;

  useEffect(() => {
    if (areActiveFromUrl === "false") {
      setAreActive(false);
      return;
    }
    if (areActiveFromUrl === "true") {
      setAreActive(true);
    }
  }, [areActiveFromUrl]);

  const {
    data: trucksTable,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ["trucksTable"],
    queryFn: () => fetchTrucks(token, areActive),
  });

  const queryClient = useQueryClient();

  useEffect(() => {
    const truckConnection = new signalR.HubConnectionBuilder()
      .withUrl(`${apiVariable}/truckHub`, {
        accessTokenFactory: () => token || "",
        withCredentials: false,
      })
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.None)
      .build();

    truckConnection.on("TruckUpdated", () => {
      queryClient.invalidateQueries({ queryKey: ["trucksTable"] });
    });

    truckConnection.start();

    return () => {
      truckConnection.stop();
    };
  }, [token, queryClient]);

  useEffect(() => {
    if (token) {
      refetch();
    }
  }, [areActive, token, refetch]);

  const onRowClick = (params: any, event: React.MouseEvent) => {
    const url = `truck/${params.row.id}`;

    if (event.button === 1 || event.ctrlKey || event.metaKey) {
      event.preventDefault();
      event.stopPropagation();

      window.open("assets/" + url, "_blank");
    } else {
      navigate(url);
    }
  };

  const handlePageChange = (newPage: number) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("page", newPage.toString());
    setSearchParams(nextParams);
  };

  const filteredTrucks = useMemo(() => {
    if (!trucksTable) return [];
    const nowYear = new Date().getFullYear();
    const search = normalize(searchTerm);

    return trucksTable.filter((truck: any) => {
      const matchesSearch = [
        truck.unitNumber,
        truck.vin,
        truck.licencePlate,
      ].some((field) => normalize(field).includes(search));
      if (!matchesSearch) return false;

      if (
        statusFromUrl &&
        !normalizeWithSpaces(truck.status).includes(
          normalizeWithSpaces(statusFromUrl),
        )
      ) {
        return false;
      }

      if (
        transactionTypeFromUrl &&
        !normalizeWithSpaces(truck.transactionType).includes(
          normalizeWithSpaces(transactionTypeFromUrl),
        )
      ) {
        return false;
      }

      if (excludeStatuses.length > 0) {
        const currentStatus = normalize(truck.status);
        if (excludeStatuses.includes(currentStatus)) {
          return false;
        }
      }

      if (companyIdFromUrl) {
        if (filterByAuthorityCompanyIdFromUrl) {
          const authorityCompanyId = truck.authorityCompanyId ?? null;
          if (String(authorityCompanyId ?? "") !== companyIdFromUrl) {
            return false;
          }
        } else if (String(truck.companyOwnerId ?? "") !== companyIdFromUrl) {
          return false;
        }
      }

      if (ageBucketFromUrl) {
        const normalizedAgeBucket = normalizeWithSpaces(ageBucketFromUrl);
        const truckYear =
          truck.year == null ? null : parseNumberToken(String(truck.year));
        const truckAge = truckYear == null ? null : nowYear - truckYear;
        const looksLikeCalendarYear = /\b(19|20)\d{2}\b/.test(
          normalizedAgeBucket,
        );
        const looksLikeAgeBucket =
          normalizedAgeBucket.includes("year") ||
          normalizedAgeBucket.includes("yr") ||
          normalizedAgeBucket.includes("age");

        if (looksLikeCalendarYear) {
          if (!matchRangeLabel(truckYear, ageBucketFromUrl)) {
            return false;
          }
        } else if (looksLikeAgeBucket) {
          if (!matchFleetAgeBucket(truckAge, ageBucketFromUrl)) {
            return false;
          }
        } else if (
          !matchFleetAgeBucket(truckAge, ageBucketFromUrl) &&
          !matchRangeLabel(truckYear, ageBucketFromUrl)
        ) {
          return false;
        }
      }

      if (odometerBucketFromUrl) {
        const mileage =
          truck.mileage == null
            ? null
            : parseNumberToken(String(truck.mileage));
        if (!matchFleetOdometerBucket(mileage, odometerBucketFromUrl)) {
          return false;
        }
      }

      if (makeFromUrl && normalize(truck.make) !== normalize(makeFromUrl)) {
        return false;
      }

      return true;
    });
  }, [
    trucksTable,
    searchTerm,
    statusFromUrl,
    transactionTypeFromUrl,
    excludeStatuses,
    ageBucketFromUrl,
    odometerBucketFromUrl,
    makeFromUrl,
    companyIdFromUrl,
    filterByAuthorityCompanyIdFromUrl,
  ]);

  const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAreActive(event.target.checked);
  };

  return (
    <Box className="trucks-table" height={900}>
      <Box
        className="table-controls"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 3,
        }}
      >
        <FormControlLabel
          control={
            <Switch
              checked={areActive}
              onChange={handleSwitchChange}
              color="primary"
            />
          }
          label={`${areActive ? "Active" : "Inactive"} Trucks`}
        />

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            position: "relative",
          }}
        >
          <TextField
            label="Search"
            variant="outlined"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by Unit Number, VIN, Licence Plate"
            sx={{ maxWidth: "100%", width: 400 }}
          />
        </Box>
      </Box>

      {isLoading ? (
        <Box className="table-loader">
          <CircularProgress />
        </Box>
      ) : isError ? (
        <Box className="table-error">
          <Typography color="error">Error: {(error as any).message}</Typography>
        </Box>
      ) : (
        <PersistedDataGrid
          tableName="trucks"
          rows={filteredTrucks}
          columns={trucksTableColumns}
          compactViewConfig={{
            primaryField: "unitNumber",
            secondaryFields: ["make", "model"],
            detailFields: ["vin", "year", "transactionType"],
            actionFields: ["delete"],
            statusField: "status",
          }}
          applyFilterModel={applyFilterModel}
          pageSizeOptions={[10]}
          paginationModel={{ page: pageFromUrl, pageSize: 10 }}
          paginationMode="client"
          onPaginationModelChange={(m) => handlePageChange(m.page)}
          onRowClick={onRowClick}
          sx={{ height: 700 }}
        />
      )}
    </Box>
  );
};

export default TrucksTable;
